import { useState, useEffect } from 'react';
import { getStats, updateCommission } from '../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const COLORS = ['#e94560', '#0f3460', '#16213e', '#1a1a2e'];

export default function Dashboard({ userRole }) {
  const isSuperAdmin = userRole === 'super_admin';
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [commission, setCommission] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadStats(); }, []);

  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getStats();
      setStats(data);
      setCommission(data.commission_percent);
    } catch (e) {
      const msg = e?.response?.data?.detail
        || e?.message
        || 'Failed to load stats. Check if backend is running on localhost:8000';
      setError(msg);
      console.error('Dashboard load error:', e);
    }
    setLoading(false);
  };

  const handleCommission = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateCommission(parseFloat(commission));
      alert('Commission updated successfully!');
      loadStats();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed to update commission'); }
    setSaving(false);
  };

  if (loading) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ width: 40, height: 40, border: '4px solid #eee', borderTop: '4px solid #6C63FF', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <p style={{ marginTop: 16, color: '#888' }}>Loading dashboard...</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
      <h3 style={{ color: '#d32f2f', marginBottom: 8 }}>Dashboard Error</h3>
      <p style={{ color: '#666', marginBottom: 16, textAlign: 'center', maxWidth: 400 }}>{error}</p>
      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={loadStats} style={{ padding: '10px 24px', background: '#6C63FF', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 'bold' }}>🔄 Retry</button>
        <button onClick={() => { localStorage.removeItem('admin_token'); localStorage.removeItem('admin_user'); window.location.reload(); }}
          style={{ padding: '10px 24px', background: '#fee', color: '#d32f2f', border: '1px solid #fcc', borderRadius: 10, cursor: 'pointer', fontWeight: 'bold' }}>🔑 Re-login</button>
      </div>
    </div>
  );

  if (!stats) return null;

  const barData = [
    { name: 'Users', value: stats.total_users },
    { name: 'Workers', value: stats.total_workers },
    { name: 'Bookings', value: stats.total_bookings },
    { name: 'Pending', value: stats.pending_bookings },
  ];

  const pieData = [
    { name: 'Completed', value: stats.total_bookings - stats.pending_bookings },
    { name: 'Pending', value: stats.pending_bookings },
  ];

  const statCards = [
    { label: 'Total Customers', value: stats.total_users, icon: '👥', color: '#e3f2fd', iconBg: '#1565c0' },
    { label: 'New This Month', value: stats.new_customers_month || 0, icon: '🆕', color: '#e8f5e9', iconBg: '#2e7d32' },
    { label: 'Repeat Customers', value: stats.repeat_customers || 0, icon: '🔁', color: '#fff3e0', iconBg: '#e65100' },
    { label: 'Repeat %', value: `${stats.repeat_percent || 0}%`, icon: '📈', color: '#fce4ec', iconBg: '#c62828' },
    { label: 'Total Workers', value: stats.total_workers, icon: '🔧', color: '#e8f5e9', iconBg: '#2e7d32' },
    { label: 'Total Bookings', value: stats.total_bookings, icon: '📋', color: '#fff8e1', iconBg: '#f57f17' },
    ...(isSuperAdmin ? [
      { label: 'Platform Revenue', value: `₹${Number(stats.total_revenue || 0).toFixed(2)}`, icon: '💰', color: '#fce4ec', iconBg: '#c62828' },
      { label: 'Total Refunded', value: `₹${Number(stats.total_refunded || 0).toFixed(2)}`, icon: '↩️', color: '#ffebee', iconBg: '#b71c1c' },
      { label: 'Net Revenue', value: `₹${Number(stats.net_revenue || stats.total_revenue || 0).toFixed(2)}`, icon: '📈', color: '#e0f2f1', iconBg: '#00695c' },
    ] : []),
    { label: 'Pending Bookings', value: stats.pending_bookings, icon: '⏳', color: '#f3e5f5', iconBg: '#6a1b9a' },
    ...(isSuperAdmin ? [
      { label: 'Commission', value: `${Number(stats.commission_percent).toFixed(2)}%`, icon: '📊', color: '#e8eaf6', iconBg: '#283593' },
    ] : []),
  ];

  return (
    <div>
      <div className="page-header">
        <h1>Dashboard Overview</h1>
        <p>Welcome back! Here's what's happening with UrbanChamp.</p>
      </div>

      <div className="stats-grid">
        {statCards.map((s, i) => (
          <div className="stat-card" key={i}>
            <div className="stat-icon" style={{ background: s.color }}>
              <span style={{ fontSize: 24 }}>{s.icon}</span>
            </div>
            <div className="stat-info">
              <h3>{s.value}</h3>
              <p>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <div className="card">
          <div className="card-header"><h2>📊 Overview Chart</h2></div>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={barData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#e94560" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <div className="card-header"><h2>🥧 Bookings Status</h2></div>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label>
                {pieData.map((_, i) => <Cell key={i} fill={COLORS[i]} />)}
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {isSuperAdmin && (
        <div className="card">
          <div className="card-header"><h2>⚙️ Commission Settings</h2></div>
          <form onSubmit={handleCommission} className="commission-form">
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Platform Commission (%)</label>
              <input type="number" value={commission} onChange={e => setCommission(e.target.value)} min="0" max="100" step="0.1" />
            </div>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Update Commission'}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
